'use client'

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import { Property } from '@/payload-types'
import { formatPriceDZD } from '@/utilities/PriceFormat'

type PropertyDescriptionProps = {
  className?: string
  description?: string
  property: Property
  dictionary: any
  lang?: string
}

const PropertyDescription = ({
  className,
  description,
  property,
  dictionary,
  lang = 'en',
}: PropertyDescriptionProps) => {
  const [showFullDescription, setShowFullDescription] = useState(false)

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription)
  }

  return (
    <div className={cn('bg-card rounded-xl container mx-auto p-[30px] shadow-sm ', className)}>
      {/* Property Description */}
      <div className="mb-10 flex flex-col gap-5">
        <h2 className="text-typography text-[17px] font-semibold">
          {dictionary?.propertyDetail?.propertyDescription || 'Property Description'}
        </h2>
        <div className="flex flex-col gap-4">
          <p
            className={`text-sm text-descriptionColor leading-7 ${showFullDescription ? '' : 'line-clamp-5'}`}
          >
            {description}
          </p>
          {description && description?.length > 380 && (
            <button
              onClick={toggleDescription}
              className="w-fit text-typography font-medium hover:underline underline"
            >
              {showFullDescription
                ? dictionary?.propertyDetail?.showLess || 'Show less'
                : dictionary?.propertyDetail?.showMore || 'Show more'}
            </button>
          )}
        </div>
      </div>

      {/* Property Details */}
      <div className="mb-10 flex flex-col gap-5">
        <h2 className="text-typography text-[17px] font-semibold">
          {dictionary?.propertyDetail?.propertyDetails || 'Property Details'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-5 gap-y-5 gap-x-20">
          <div className="grid grid-cols-2 pb-4 border-b border-gray-200">
            <span className="text-sm font-semibold text-typography">
              {dictionary?.propertyDetail?.price || 'Price'}
            </span>
            <span className="text-descriptionColor text-sm">
              {formatPriceDZD(property?.propertyDescription?.price, lang)}
            </span>
          </div>

          <div className="grid grid-cols-2 pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-typography">
              {dictionary?.propertyDetail?.addedOn || 'Added on'}
            </span>
            <span className="text-descriptionColor text-sm">
              {property?.createdAt
                ? new Date(property.createdAt).toLocaleDateString(lang, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : 'N/A'}
            </span>
          </div>

          <div className="grid grid-cols-2 pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-typography">
              {dictionary?.propertyDetail?.propertySize || 'Property Size'}
            </span>
            <span className="text-descriptionColor text-sm">
              {property?.detailSection?.surface}
            </span>
          </div>

          <div className="grid grid-cols-2  row-start-5 pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-typography">
              {dictionary?.propertyDetail?.condition || 'Condition'}
            </span>
            <span className="text-descriptionColor text-sm">
              {property?.detailSection?.condition
                ? dictionary?.propertyDetail?.conditionValues?.[property.detailSection.condition] ||
                  property.detailSection.condition
                : 'N/A'}
            </span>
          </div>

          <div className="grid grid-cols-2 row-start-4 pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-typography">
              {dictionary?.propertyDetail?.bedrooms || 'Bedrooms'}
            </span>
            <span className="text-descriptionColor text-sm">
              {property?.detailSection?.rooms || 'N/A'}
            </span>
          </div>

          <div className="grid grid-cols-2 pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-typography">
              {dictionary?.propertyDetail?.bathrooms || 'Bathrooms'}
            </span>
            <span className="text-descriptionColor text-sm">
              {property?.detailSection?.bathrooms || 'N/A'}
            </span>
          </div>

          <div className="grid grid-cols-2 pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-typography">
              {dictionary?.propertyDetail?.furnishing || 'Furnishing'}
            </span>
            <span className="text-descriptionColor text-sm">
              {property?.detailSection?.furnishing
                ? dictionary?.propertyDetail?.furnishingValues?.[
                    property.detailSection.furnishing
                  ] || property.detailSection.furnishing
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PropertyDescription
